import { matchPath } from 'react-router-dom';

/**
 * Feature Registry —— 全系统功能资产的唯一真源。
 *
 * 用途：由同一份定义约束 Router / Sidebar / Breadcrumb / 权限 / 深链，
 * 避免「菜单、路由、权限」多处各自维护导致归属漂移。
 *
 * 设计依据：信息架构 V2（工作台 / 业务 / 平台 / 系统 四区）。
 * - DOMAIN：只服务单一业务域（配置影响面局限于该域）
 * - PLATFORM：被多个业务域复用的基础能力（资源所有权，非使用关系）
 * - SYSTEM：身份、集成、安全合规、审计、诊断等系统级治理
 *
 * 归属判断标准：看它修改的业务对象是谁，而非看它叫什么名字、由谁维护、URL 在哪。
 * legacyLocations 仅记录历史位置，不直接驱动重定向——同一旧 URL 常对应多个新 Feature。
 * 确定的一对一旧地址迁移登记在 LEGACY_REDIRECTS；带 Tab 的旧地址登记在 LEGACY_TAB_REDIRECTS。
 * nav 为可选：只有真正出现在侧栏的 Feature 才登记，详情页与工作台内面板不登记。
 * permission 为 sys_permission(type=menu) 的码；null 表示暂无独立权限点（随域放行）。
 */

import { FEATURES_PART_1 } from './features/_part-1';
import { FEATURES_PART_2 } from './features/_part-2';
import { FEATURES_PART_3 } from './features/_part-3';
import { FEATURES_PART_4 } from './features/_part-4';
import { FEATURES_PART_5 } from './features/_part-5';
import { FEATURES_PART_6 } from './features/_part-6';

/** 归属层级 */
export type Scope = 'domain' | 'platform' | 'system';

/** 功能类型 */
export type FeatureType =
  | 'workspace'
  | 'resource'
  | 'record'
  | 'config'
  | 'analytics'
  | 'governance'
  | 'platform-capability';

/** 导航分区（Sidebar 一级 Section） */
export type NavSection = 'workbench' | 'business' | 'platform' | 'system';

/** 迁移优先级：P0 明显错误/持续制造混乱，P1 结构问题，P2 优化项 */
export type Priority = 'P0' | 'P1' | 'P2';

export interface FeatureNav {
  section: NavSection;
  /** 域内分组 key（如 outbound / settings）；缺省=域根 */
  group?: string;
  /** 同组内排序 */
  order: number;
  /** 单条子项直接作为分区子项渲染，不再包一层分组（如 业务 → 报表中心） */
  flat?: boolean;
  /**
   * 暂不上侧栏：条目仍完整留在 Registry，**只是侧栏不渲染它**。
   * 页头名解析（§14）、模块功能地图（Ctrl+M）、授权树（menuTree）与深链访问照旧，
   * 因此「隐藏菜单」不等于「下线功能」——下线要删 `menuTree.ts` 的节点并走发布单元 SQL，
   * 那个动作会同时撤掉权限码与授权界面里的勾选项。
   * 侧栏投影的唯一出口是 `registry/sidebarNav.ts`（`scripts/check-nav-perms.mjs` 仍按 Registry 全校验权限码）。
   */
  hidden?: boolean;
}

export interface Feature {
  /** 稳定英文 id，`domain.resource` 形式 */
  id: string;
  title: string;
  domain: string;
  scope: Scope;
  type: FeatureType;
  /** 目标（canonical）Route */
  route: string;
  /** 历史位置记录（仅供追溯，不直接驱动重定向） */
  legacyLocations?: string[];
  /**
   * 无独立落点：路由未挂载，或只是指向别处的重定向别名。
   * 这类 Feature 仍留在 Registry（供溯源与重定向表引用），但**不参与导航与功能检索**——
   * 纳进地图会给出「点了跳到别处/原地不动」的结果。
   * 判定不由人工说了算：`scripts/check-feature-index.mjs` 会读 `App.tsx` 的真实路由表校验，
   * 该路由一旦被挂载（或已挂载的却被标成 orphan），门禁即报错。
   */
  orphan?: boolean;
  /**
   * 所需权限码：单个码 / 「任一命中」码组 / null=不设权限（公共页）。
   * 判据与用法见 `registry/access.ts`——**有 nav 的条目不允许 null**（等于对全角色可见），
   * 由 `scripts/check-nav-perms.mjs` 门禁拦住。
   * 码组用于「设置 Landing」：入口对「持有任一子配置权限」的角色可见，页内 Tab 再按各自子码裁剪。
   */
  permission: string | string[] | null;
  /** 导航登记：仅真正出现在侧栏的 Feature 才写；详情页/工作台内面板不写 */
  nav?: FeatureNav;
  priority: Priority;
  /** 现状归属问题说明（缺省=无问题） */
  issue?: string;
}

/** 一对一旧地址迁移：仅登记可凭 pathname 唯一判定的重定向 */
export interface LegacyRedirect {
  from: string;
  toFeatureId: string;
}

/** 带 Tab 的旧地址迁移：同一 pathname 下按 query 区分目标 */
export interface LegacyTabRedirect {
  pathname: string;
  tab: string;
  toFeatureId: string;
}

/** 侧栏分区（与 FEATURES 分离：分区本身不是 Feature） */
export const NAV_SECTIONS: Array<{ id: NavSection; title: string }> = [
  { id: 'workbench', title: '工作台' },
  { id: 'business', title: '业务' },
  { id: 'platform', title: '平台' },
  { id: 'system', title: '系统' },
];

/** 顶层导航锚点（仪表盘/坐席工作台等跨域首页入口；非 Feature，无业务归属） */
export interface NavAnchor {
  id: string;
  title: string;
  route: string;
  permission: string | string[] | null;
  /** 可见角色白名单；给定则优先于 permission */
  roles?: string[];
  /**
   * 需绑定坐席档案（`agent.user_id` = 当前登录用户）才可见。
   * 与角色无关的**账号能力**条件：坐席工作台是作业面（软电话/接待/辅助），
   * 未绑坐席的账号（内置 admin、未绑档案的主管）进去只有空壳——故入口不显示。
   * 判据由 `registry/access.ts::canSeeEntry` 统一实现，绑定态取自 `store/agent`（GET /agents/me）。
   */
  requiresAgent?: boolean;
}

export const NAV_ANCHORS: NavAnchor[] = [
  { id: 'dashboard', title: '仪表盘', route: '/dashboard', permission: null },
  { id: 'workbench', title: '坐席工作台', route: '/workbench', permission: 'workbench', requiresAgent: true },
  { id: 'inbox', title: '待办中心', route: '/workbench/tasks', permission: 'workflow.inbox' },
];

/** 业务域显示名（侧栏分组标题 / 面包屑用；域 key 与 Feature.domain 一致） */
export const DOMAIN_LABELS: Record<string, string> = {
  outbound: '外呼',
  chat: '在线客服',
  inbound: '呼入',
  bot: '智能机器人',
  assist: '坐席辅助',
  quality: '智能质检',
  knowledge: '知识库',
  ticketing: '客户与工单',
  coach: '智能演练',
  operations: '运营监控',
  'customer-insights': '客户洞察',
  analytics: '报表中心',
  ai: 'AI 能力',
  telephony: '通信能力',
  workflow: '流程中心',
  'data-collection': '数据采集',
  identity: '用户与组织',
  integration: '集成',
  compliance: '安全与合规',
  audit: '审计',
  diagnostics: '故障诊断',
  notice: '公告',
};

/**
 * 各分区内域分组的显式顺序（侧栏/文档生成共用）。
 * 域顺序是产品决策，不应依赖 FEATURES 数组顺序或 nav.order 的巧合——
 * 显式声明后，调整顺序只需改这里；未登记的域按首次出现顺序追加，避免新增域静默排到末尾。
 */
export const DOMAIN_ORDER: Record<NavSection, string[]> = {
  workbench: [],
  business: [
    'inbound',
    'chat',
    'outbound',
    'bot',
    'knowledge',
    'assist',
    'quality',
    'ticketing',
    'coach',
    'operations',
    'customer-insights',
    'analytics',
  ],
  platform: ['ai', 'telephony', 'workflow', 'data-collection'],
  system: ['identity', 'compliance', 'audit', 'integration', 'diagnostics', 'notice'],
};

export const FEATURES: Feature[] = [
  ...FEATURES_PART_1,
  ...FEATURES_PART_2,
  ...FEATURES_PART_3,
  ...FEATURES_PART_4,
  ...FEATURES_PART_5,
  ...FEATURES_PART_6,
];

/**
 * 一对一旧地址迁移表：仅登记可凭 pathname 唯一判定的重定向。
 * 一对多旧地址（/aicenter、/report、/outbound、/system/sys-settings、/account、/ops-center）
 * 不在此登记——去向依赖 Tab/上下文，交由各自迁移规则处理，避免特殊判断扩散。
 */
export const LEGACY_REDIRECTS: LegacyRedirect[] = [
  { from: '/calls', toFeatureId: 'inbound.calls' },
  { from: '/calls/transcripts', toFeatureId: 'inbound.transcripts' },
  { from: '/calls/blacklist', toFeatureId: 'inbound.blacklist' },
  { from: '/chat', toFeatureId: 'chat.sessions' },
  { from: '/chat/bot', toFeatureId: 'bot.chat-robots' },
  { from: '/chatbot', toFeatureId: 'bot.chat-robots' },
  { from: '/inbound/sms', toFeatureId: 'telephony.sms' },
  { from: '/scripts', toFeatureId: 'assist.scripts' },
  { from: '/assist/config', toFeatureId: 'assist.settings' },
  { from: '/intent', toFeatureId: 'bot.intents' },
  { from: '/bot', toFeatureId: 'bot.robots' },
  { from: '/quality', toFeatureId: 'quality.reports' },
  { from: '/crm', toFeatureId: 'ticketing.customers' },
  { from: '/workflow', toFeatureId: 'workflow.inbox' },
  { from: '/shift', toFeatureId: 'operations.shifts' },
  { from: '/monitor/screen', toFeatureId: 'operations.screen' },
  { from: '/ops-center/settings', toFeatureId: 'operations.settings.alerts' },
  { from: '/satisfaction', toFeatureId: 'customer-insights.satisfaction' },
  { from: '/satisfaction/config', toFeatureId: 'customer-insights.settings.satisfaction' },
  { from: '/voc', toFeatureId: 'customer-insights.voc' },
  { from: '/system/voc-labels', toFeatureId: 'customer-insights.settings.voc-labels' },
  { from: '/system/ivr-menus', toFeatureId: 'telephony.routing.ivr' },
  { from: '/inbound/ivr', toFeatureId: 'telephony.routing.ivr' },
  { from: '/system/routes', toFeatureId: 'telephony.routing' },
  { from: '/inbound/routes', toFeatureId: 'telephony.routing' },
  { from: '/system/skill-groups', toFeatureId: 'identity.skill-groups' },
  { from: '/inbound/skill-groups', toFeatureId: 'identity.skill-groups' },
  { from: '/system/sip-gateways', toFeatureId: 'telephony.gateways' },
  { from: '/system/sip-settings', toFeatureId: 'telephony.sip' },
  { from: '/system/trunk-accounts', toFeatureId: 'telephony.trunks' },
  { from: '/system/audio-files', toFeatureId: 'telephony.audio' },
  { from: '/system/ticket-fields', toFeatureId: 'ticketing.settings.fields' },
  { from: '/system/ticket-templates', toFeatureId: 'ticketing.settings.templates' },
  { from: '/system/workflows', toFeatureId: 'workflow.approvals' },
  { from: '/system/flows', toFeatureId: 'workflow.flows' },
  { from: '/system/collect-fields', toFeatureId: 'data-collection.fields' },
  { from: '/system/collection', toFeatureId: 'data-collection.rules' },
  // 开放平台已整体下线：`/system/openplatform` 不再登记重定向（老地址一律落到 `*` 兜底）。
  { from: '/system/data-sources', toFeatureId: 'integration.data-sources' },
  { from: '/system/egress', toFeatureId: 'compliance.egress' },
  { from: '/system/audit-logs', toFeatureId: 'audit.logs' },
  { from: '/system/diag', toFeatureId: 'diagnostics.diag' },
];

/** 带 Tab 的旧地址迁移表：同一 pathname 下按 query 区分目标（如 /quality?tab=rules） */
export const LEGACY_TAB_REDIRECTS: LegacyTabRedirect[] = [
  { pathname: '/quality', tab: 'appeals', toFeatureId: 'quality.appeals' },
  { pathname: '/quality', tab: 'realtime', toFeatureId: 'quality.realtime' },
  { pathname: '/quality', tab: 'flow-reports', toFeatureId: 'quality.flow-reports' },
  { pathname: '/quality/analytics', tab: 'realtime', toFeatureId: 'quality.realtime' },
  { pathname: '/quality/analytics', tab: 'flow-reports', toFeatureId: 'quality.flow-reports' },
  { pathname: '/quality', tab: 'sampling', toFeatureId: 'quality.sampling' },
  { pathname: '/quality', tab: 'samples', toFeatureId: 'quality.samples' },
  { pathname: '/quality', tab: 'rules', toFeatureId: 'quality.rules' },
  { pathname: '/quality', tab: 'stats', toFeatureId: 'quality.analytics' },
  { pathname: '/coach', tab: 'history', toFeatureId: 'coach.history' },
  { pathname: '/coach', tab: 'bank', toFeatureId: 'coach.bank' },
  { pathname: '/ops-center', tab: 'agents', toFeatureId: 'operations.agents' },
  { pathname: '/ops-center', tab: 'queues', toFeatureId: 'operations.queues' },
  { pathname: '/ops-center', tab: 'cockpit', toFeatureId: 'operations.cockpit' },
  { pathname: '/outbound', tab: 'scenarios', toFeatureId: 'outbound.scenes' },
];

/** 按导航分区取功能（供 Sidebar；仅含登记了 nav 的 Feature） */
export function featuresBySection(section: NavSection): Feature[] {
  return FEATURES.filter((f) => f.nav?.section === section);
}

export interface NavGroup {
  key: string;
  title: string;
  items: Feature[];
  /** 该分组直接平铺为分区子项（不渲染分组头） */
  flat?: boolean;
}

export interface NavSectionGroup {
  section: NavSection;
  title: string;
  groups: NavGroup[];
}

/**
 * 按「分区 → 域 → 条目」组织侧栏数据（供 Sidebar / 导航预览）。
 * 工作台为聚合容器，不按域分组；业务/平台/系统按域分组。
 */
export function groupedNav(): NavSectionGroup[] {
  return NAV_SECTIONS.map((s) => {
    const items = FEATURES.filter((f) => f.nav?.section === s.id).sort(
      (a, b) => (a.nav?.order ?? 0) - (b.nav?.order ?? 0),
    );
    if (s.id === 'workbench') {
      return { section: s.id, title: s.title, groups: [{ key: 'workbench', title: '', items }] };
    }
    const byDomain = new Map<string, Feature[]>();
    for (const f of items) {
      const arr = byDomain.get(f.domain) ?? [];
      arr.push(f);
      byDomain.set(f.domain, arr);
    }
    // 域顺序由 DOMAIN_ORDER 显式声明；未登记的域按首次出现顺序追加（见该常量注释）
    const order = DOMAIN_ORDER[s.id];
    const orderedKeys = [
      ...order.filter((d) => byDomain.has(d)),
      ...[...byDomain.keys()].filter((d) => !order.includes(d)),
    ];
    const groups: NavGroup[] = orderedKeys.map((domain) => {
      const fs = byDomain.get(domain)!;
      return {
        key: domain,
        title: DOMAIN_LABELS[domain] ?? domain,
        items: fs,
        flat: fs.every((f) => f.nav?.flat === true),
      };
    });
    return { section: s.id, title: s.title, groups };
  });
}

export interface NavDomain {
  key: string;
  title: string;
  items: Feature[];
}

/**
 * 全量域列表（跨分区、按导航顺序）：供「锚点 + 各域作一级」的扁平大菜单使用。
 * 与 groupedNav 同源，只抹平 NAV_SECTIONS（工作台/业务/平台/系统）这一层。
 */
export function flatNav(): NavDomain[] {
  const out: NavDomain[] = [];
  for (const s of groupedNav()) {
    for (const g of s.groups) {
      if (g.items.length === 0) continue;
      out.push({ key: g.key, title: g.title || DOMAIN_LABELS[g.key] || g.key, items: g.items });
    }
  }
  return out;
}

/** 按业务域取功能（供域内菜单 / 权限） */
export function featuresByDomain(domain: string): Feature[] {
  return FEATURES.filter((f) => f.domain === domain);
}

/** 按 id 取功能（供 Router / 深链） */
export function featureById(id: string): Feature | undefined {
  return FEATURES.find((f) => f.id === id);
}

/**
 * 由真实 pathname 反查功能（供 Breadcrumb / 页面归属）。
 * 用 react-router 的 matchPath 支持动态段（/bot/robots/:id 匹配 /bot/robots/123），不自行实现 Route Matcher。
 * 共享 Route 时（如 /workbench 由多个工作台面板共享）只返回唯一的可导航项；无法唯一判定时返回 undefined，
 * 由调用方回退到 NAV_SECTIONS（聚合容器不谎称某个 Domain 拥有整页）。
 */
export function featureByPathname(pathname: string): Feature | undefined {
  const matches = FEATURES.filter(
    (feature) => matchPath({ path: feature.route, end: true }, pathname) !== null,
  );
  if (matches.length === 1) return matches[0];
  const navigable = matches.filter((feature) => feature.nav !== undefined);
  return navigable.length === 1 ? navigable[0] : undefined;
}

const stripQuery = (p: string) => p.split('?')[0];

/**
 * 页头名解析（唯一入口，供 `PageContainer` 与命名门禁共用）。
 *
 * 只认「用户从全局导航上能点到的名字」：顶层锚点（仪表盘/坐席工作台/待办中心）与侧栏条目
 * （登记了 `nav` 的 Feature），canonical 路由与旧地址都算——页头名必须与菜单名同名，
 * 否则就是「照菜单点进来却看到另一个名字」。
 *
 * 只认这两类、不遍历全部 Feature 的原因：非侧栏条目（详情页/设置子页/工作台内面板）的 `title`
 * 是信息架构登记用语（如「对话流程（使用关系）」「场景详情（话术/转人工/结果字段/AI 策略）」），
 * 不是界面名，拿来当页头会出现「页头写着注释」。
 *
 * 解析不出（旧地址一对多、详情页、全屏页）返回 undefined，由页面自己的兜底标题接手。
 */
export function navTitleOf(pathname: string): string | undefined {
  const anchor = NAV_ANCHORS.find((a) => a.route === pathname);
  if (anchor) return anchor.title;
  const hit = featureByPathname(pathname);
  if (hit?.nav) return hit.title;
  // 旧地址（/calls、/chat…）：在侧栏条目里按 legacyLocations 反查；多义时交给页面兜底
  const legacy = FEATURES.filter(
    (f) => f.nav && (f.legacyLocations ?? []).some((loc) => stripQuery(loc) === pathname),
  );
  return legacy.length === 1 ? legacy[0].title : undefined;
}

/** 页头名的域名后缀（浏览器标签用，如「质检 · 智能质检」）：业务域显示名优先，退回导航分区名 */
export function navDomainLabelOf(pathname: string): string | undefined {
  const hit = featureByPathname(pathname);
  if (!hit) return undefined;
  return DOMAIN_LABELS[hit.domain] ?? NAV_SECTIONS.find((s) => s.id === hit.nav?.section)?.title;
}

/** 解析一对一旧地址重定向（供 Redirect 表生成） */
export function resolveLegacyRedirect(pathname: string): Feature | undefined {
  const hit = LEGACY_REDIRECTS.find((r) => r.from === pathname);
  return hit ? featureById(hit.toFeatureId) : undefined;
}

/** 解析带 Tab 的旧地址重定向（供 Redirect 表生成） */
export function resolveLegacyTabRedirect(
  pathname: string,
  tab: string | null,
): Feature | undefined {
  if (!tab) return undefined;
  const hit = LEGACY_TAB_REDIRECTS.find((r) => r.pathname === pathname && r.tab === tab);
  return hit ? featureById(hit.toFeatureId) : undefined;
}

/**
 * 旧地址解析总入口（调用方只允许用此函数）。
 * 固化不变量：specific（Tab 规则）优先于 generic（Path 规则），避免 /quality?tab=rules 被 /quality 的规则抢先命中。
 */
export function resolveLegacyLocation(
  pathname: string,
  tab: string | null,
): Feature | undefined {
  return resolveLegacyTabRedirect(pathname, tab) ?? resolveLegacyRedirect(pathname);
}

/** P0 归属问题清单（供迁移排期） */
export function p0Features(): Feature[] {
  return FEATURES.filter((f) => f.priority === 'P0');
}

/**
 * 注册表自检（供单测 / 启动期断言）：返回契约违例清单，空数组=通过。
 * 校验：id 唯一、重定向目标存在、同分区无重复导航 Route、无自重定向、无重复重定向源。
 */
export function validateRegistry(): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  for (const f of FEATURES) {
    if (ids.has(f.id)) errors.push(`duplicate feature id: ${f.id}`);
    ids.add(f.id);
  }
  for (const r of [...LEGACY_REDIRECTS, ...LEGACY_TAB_REDIRECTS]) {
    if (!ids.has(r.toFeatureId)) errors.push(`redirect target missing: ${r.toFeatureId}`);
  }
  const navRoutes = new Map<string, string>();
  for (const f of FEATURES) {
    if (!f.nav) continue;
    if (f.route.includes(':')) {
      errors.push(`dynamic route cannot be sidebar item: ${f.id}`);
    }
    const key = `${f.nav.section}:${f.route}`;
    const prev = navRoutes.get(key);
    if (prev) errors.push(`duplicate nav route in ${f.nav.section}: ${f.route} (${prev} & ${f.id})`);
    else navRoutes.set(key, f.id);
  }
  for (const r of LEGACY_REDIRECTS) {
    const target = featureById(r.toFeatureId);
    if (target?.route === r.from) {
      errors.push(`self redirect: ${r.from} -> ${r.toFeatureId}`);
    }
  }
  const sources = new Set<string>();
  for (const r of LEGACY_REDIRECTS) {
    if (sources.has(r.from)) errors.push(`duplicate redirect source: ${r.from}`);
    sources.add(r.from);
  }
  return errors;
}
